import { cn } from '@lib/cn';
import { Link, useLocation } from 'react-router';

import { isTabActive } from './tab-active';

export interface Tab {
    /** Absolute pathname. The one equal to `base` is the index tab. */
    readonly to: string;
    readonly label: string;
    /**
     * How many things are behind this tab. Rendered beside the label, the way
     * the flags list already counts its type tabs — and counted from the rows
     * the screen loaded rather than from a separate endpoint, so the number
     * cannot disagree with what opening the tab shows.
     */
    readonly count?: number;
}

/**
 * A row of tabs that are routes, not component state.
 *
 * Extracted from `ClientDetailLayout` when the catalog became the second
 * caller. Each tab is a real URL, so a link opens the view it names — which is
 * the difference between this and the flags list's type tablist, where the
 * choice lives in a query parameter because the two kinds share one screen.
 *
 * `search` is carried onto every href when given. The client screens are scoped
 * by a `?env=&slot=` target, and a tab that dropped it would fall back to the
 * deployed environment on click: an operator reading a production client would
 * land on a dev one wearing the same tenant id, silently. Screens with no
 * target — the catalog has none, it is the platform's own — pass nothing.
 *
 * Active state is computed from `useLocation` rather than delegated to
 * `NavLink`: `Sidebar` already does it this way, and `end`-style matching would
 * still need a special case for an index tab, whose path is a prefix of the
 * others.
 */
export function TabStrip({
    label,
    base,
    tabs,
    search,
}: {
    /** Names the strip for assistive technology, e.g. `Client sections`. */
    readonly label: string;
    /** The index tab's path. A tab equal to it matches exactly; the rest by prefix. */
    readonly base: string;
    readonly tabs: readonly Tab[];
    readonly search?: string;
}) {
    const { pathname } = useLocation();

    return (
        <nav aria-label={label} className="mb-5 flex gap-1 border-b border-line">
            {tabs.map((tab) => {
                const active = isTabActive(pathname, tab.to, tab.to === base);
                return (
                    <Link
                        key={tab.to}
                        to={search === undefined ? tab.to : { pathname: tab.to, search }}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                            '-mb-px border-b-2 px-3.5 py-2.5 text-[13.5px] font-medium transition-colors',
                            active
                                ? 'border-brand text-accent-text'
                                : 'border-transparent text-content-muted hover:text-content',
                        )}
                    >
                        {tab.label}
                        {tab.count !== undefined && (
                            <b className="ml-1.5 font-semibold text-content-muted">{tab.count}</b>
                        )}
                    </Link>
                );
            })}
        </nav>
    );
}
